const API_ENDPOINT = 'https://api.thecreamerysportsbar.com';

export interface NewsPost {
  id: number;
  created_at: string;
  updated_at: string;
  title: string;
  content: string;
  excerpt: string | null;
  url: string;
  seo_title: string | null;
  seo_metadesc: string | null;
  seo_keyword: string | null;
}

export interface NewsResponse {
  data: NewsPost[];
  meta: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}

export async function getNews(page = 1, perPage = 9, signal?: AbortSignal): Promise<NewsResponse> {
  const response = await fetch(`${API_ENDPOINT}/pages/?page=${page}&number=${perPage}`, { signal });
  if (!response.ok) throw new Error('Failed to fetch news');
  const body = await response.json();
  if (!body || !Array.isArray(body.data) || !body.meta) throw new Error('Unexpected news response');
  return body as NewsResponse;
}

// The API answers 200 with a null body for an unknown slug, so null means "not found".
export async function getArticle(slug: string, signal?: AbortSignal): Promise<NewsPost | null> {
  const response = await fetch(`${API_ENDPOINT}/pages/${encodeURIComponent(slug)}`, { signal });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error('Failed to fetch article');
  const body = await response.json();
  return body && typeof body === 'object' ? (body as NewsPost) : null;
}

// MySQL DATETIME ("2024-01-01 12:00:00") -> "1 January 2024"
export function formatNewsDate(value: string): string {
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function summarise(post: NewsPost, maxLength = 150): string {
  const source = post.excerpt?.trim() || post.content;
  const text = (new DOMParser().parseFromString(source, 'text/html').body.textContent || '')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length <= maxLength ? text : `${text.slice(0, maxLength).trim()}...`;
}
